import * as THREE from "three/webgpu";
import {SketchEntityKind} from '../../sketch/solver/constraint/relation-compatibility/SketchEntityKinds';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import {SketchPointObject} from './SketchPointObject';
import {isSketchPointObject} from '../guards/isSketchPointObject';
import {isSketchSlotObject} from '../guards/isSketchSlotObject';
import {EllipseControlPointRole, SketchPointKind} from './ISketchObject';
import {SketchContour,} from '../meshProxy/SketchContour';
import {AbstractSketchEdgeObject, type AbstractSketchEdgeObjectOptions,} from './AbstractSketchEdgeObject';
import {PointsMaterialFactory} from '../../material/PointsMaterialFactory';
import type {SketchCloneContext} from './SketchCloneContext';
import type {SketchCollectionObject} from './SketchCollectionObject';
import type {SketchSlotObject} from './SketchSlotObject';
import {disposeSketchContour} from './clearSketchContourDisplays';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../../sketch/config/SketchGeometryToleranceConfig';
import {ensureRaycastAccelerationForObjectTree, refitRaycastAccelerationForGeometry,} from '../../raycast-acceleration';
import type {SketchEdgeGeometryRefreshOptions} from '../../../types/mesh/sketch/SketchEdgeGeometryRefreshTypes';

/**
 * 判断对象是否为草图椭圆对象。
 * @param value 待判断的 Three 对象。
 * @returns 当对象满足草图椭圆协议时返回 true。
 */
/**
 * 椭圆定义参数。
 * 说明：
 * 1. centerWorld 为世界坐标系下的圆心；
 * 2. startAngle/endAngle/clockwise 与 THREE.EllipseCurve 语义一致；
 * 3. 当 startAngle=0 且 endAngle=2π 时表示完整椭圆。
 */
export interface SketchEllipseDefinition {
    /** 椭圆圆心（世界坐标）。 */
    centerWorld: THREE.Vector3;
    /** 椭圆长半轴半径。 */
    radiusX: number;
    /** 椭圆短半轴半径。 */
    radiusY: number;
    /** 椭圆整体旋转角（弧度）。 */
    rotation: number;
    /** 起始角（弧度）。 */
    startAngle: number;
    /** 终止角（弧度）。 */
    endAngle: number;
    /** 是否顺时针采样。 */
    clockwise: boolean;
    /** 采样段数。 */
    segments?: number;
}

export interface SketchEllipseObjectOptions extends AbstractSketchEdgeObjectOptions {
    /** 创建时所属抽象参考面。 */
    referencePlane: ReferencePlane;
    /** 椭圆几何定义*/
    definition: SketchEllipseDefinition;
    /** 椭圆中心点对象。 */
    centerPoint?: SketchPointObject;

    startPoint: SketchPointObject;
    endPoint: SketchPointObject;
}

/**
 * 草图椭圆网格对象
 * 职责
 * 1. 统一承接线状实体公共行为
 * 2. 提供椭圆定义更新与几何重建能力；
 * 3. 同时覆盖完整椭圆与部分椭圆
 */
export class SketchEllipseObject extends AbstractSketchEdgeObject {
    /** 对象运行时类型标记*/
    public readonly objectType = SketchEntityKind.Ellipse;
    /**
     * 判断当前椭圆对象是否自身闭合。
     *
     * 说明：完整椭圆自身形成闭环；部分椭圆是具有真实起终点的开放椭圆弧，
     * 只有与其它草图边共同首尾连接时才能通过 `SketchCollectionObject` 形成闭合轮廓。
     */
    public get isClosedGeometry(): boolean {
        return !this.isPartialEllipse();
    }
    /** 创建时所属抽象参考面。 */
    public referencePlane: ReferencePlane;
    isSketchEllipseObject=true

    /**
     * 椭圆弧作为混合闭合链成员时绑定的闭合宿主。
     * 说明：
     * 1. 完整椭圆是自闭合实体，轮廓由自身 `contour` 承载，`closedCollection` 保持 null；
     * 2. 部分椭圆（椭圆弧）作为开放边进入 `SketchCollectionObject` 或槽口时，由该字段指向宿主；
     * 3. 该字段参与闭合候选锁定判定与 profile 恢复，语义与圆弧、抛物线等集合源一致。
     */
    public closedCollection: SketchCollectionObject | SketchSlotObject | null = null;

    /** 椭圆第一控制点*/
    private ellipsePointA: SketchPointObject;

    /** 椭圆第二控制点*/
    private ellipsePointB: SketchPointObject;

    /** 椭圆中心点对象。 */
    private ellipseCenterPoint: SketchPointObject;

    /** 椭圆几何定义*/
    private ellipseDefinition: Required<SketchEllipseDefinition>;

    /** 椭圆四向基准轴控制点映射。 */
    private readonly cardinalControlPoints = new Map<EllipseControlPointRole, SketchPointObject>();

    /** 拖拽重建防重入标志。 */
    private isRebuildingFromDrag = false;


    /** 实例计数，用于生成显示名称*/
    static count = 0;

    public get baseDisplayName(): string {
        return '椭圆';
    }

    /**
     * 构造草图椭圆对象
     * @param options 构造参数
     */
    public constructor(options: SketchEllipseObjectOptions) {
        super(options);
        this.referencePlane = options.referencePlane;
        this.ellipsePointA = options.startPoint;
        this.ellipsePointB = options.endPoint;
        this.ellipseCenterPoint = options.centerPoint ?? new SketchPointObject({
            position: options.definition.centerWorld.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane: options.referencePlane,
        });
        this.ellipsePointA.registerConnectedEdge(this);
        this.ellipsePointB.registerConnectedEdge(this);
        this.registerRelatedSketchPoints([this.ellipsePointA, this.ellipsePointB, this.ellipseCenterPoint]);
        this.ellipsePointA.attachEndpointRole();
        this.ellipsePointB.attachEndpointRole();
        this.ellipseCenterPoint.attachCurveCenterControlPointRole();
        this.ellipseDefinition = {
            ...options.definition,
            segments: options.definition.segments ?? 64,
        };
        this.name = `${this.baseDisplayName}${SketchEllipseObject.count}`;
        SketchEllipseObject.count += 1;

        /**
         * 构造完成后立即初始化椭圆几何。
         * 说明：
         * 1. 等距实体、圆角落地等链路会在创建后立刻参与显示与后续交互；
         * 2. 若此时 geometry 尚未生成，外部读取几何顶点、包围盒或调试信息时会得到空对象；
         * 3. 在构造阶段完成首次几何同步，可保证新建椭圆从创建瞬间起就是完整可渲染状态。
         */
        this.updateEllipse();
    }

    /**
     * 更新椭圆定义并刷新几何
     * @param definition 新椭圆定义
     */
    public updateEllipseDefinition(definition: SketchEllipseDefinition): void {
        this.ellipseDefinition = {
            ...definition,
            segments: definition.segments ?? this.ellipseDefinition.segments,
        };
        this.updateEllipse();
        /**
         * 定义 setter 同步维护端点、中心与轴控制点结构不变量；关系和尺寸裁决由
         * 随后的统一参数求解负责，本步骤不调用对象式约束。
         */
        this.syncControlPointsFromDefinition();
    }

    /**
     * 刷新椭圆几何
     */
    public updateEllipse(): void {
        this.updateGeometry();
    }


    /**
     * 平移椭圆（通过平移椭圆圆心与两控制点实现）
     * @param delta 位移向量
     * @throws 当椭圆的任一端点被固定时抛出错误。
     */
    public translateEllipse(delta: THREE.Vector3): void {
        if (this.ellipsePointA.isFixed() || this.ellipsePointB.isFixed()) {
            throw new Error('Ellipse endpoint is fixed and Ellipse translation is blocked.');
        }

        const nextCenter = this.ellipseDefinition.centerWorld.clone().add(delta);
        const nextPointA = this.ellipsePointA.getPosition().add(delta);
        const nextPointB = this.ellipsePointB.getPosition().add(delta);

        this.ellipseDefinition.centerWorld = nextCenter;
        this.ellipsePointA.setPosition(nextPointA);
        this.ellipsePointB.setPosition(nextPointB);
        this.updateEllipse();
    }


    /**
     * 通过对象级定义平移椭圆。
     *
     * 设计说明：
     * 1. 移动实体中的椭圆与圆弧一样，不能把中心点、端点和象限控制点当作普通点逐个移动；
     * 2. 中心点移动会触发椭圆定义重建，如果后续端点和象限点再次逐点移动，会造成半轴或端点参数被错误改写；
     * 3. 因此这里以当前椭圆定义为基线，只平移定义中心和所有控制点显示位置，保持半轴、旋转角、起终角不变。
     *
     * @param delta 位移向量。
     * @throws 当椭圆本身或任一关键控制点不允许拖动时抛出错误。
     */
    public translateEllipseByCenterPoint(delta: THREE.Vector3): void {
        if (!this.canDrag()) {
            throw new Error('Ellipse control point is fixed and center point translation is blocked.');
        }

        // 保持几何定义除中心外完全不变，只更新中心世界坐标。
        this.ellipseDefinition = {
            ...this.ellipseDefinition,
            centerWorld: this.ellipseDefinition.centerWorld.clone().add(delta),
        };

        // 静默平移基础控制点，避免触发点拖拽重建分支造成半轴变化。
        this.ellipseCenterPoint.syncPositionSilently(this.ellipseCenterPoint.getPosition().add(delta));
        this.ellipsePointA.syncPositionSilently(this.ellipsePointA.getPosition().add(delta));
        this.ellipsePointB.syncPositionSilently(this.ellipsePointB.getPosition().add(delta));

        // 已注册的长短轴象限控制点也跟随整体平移，保持捕捉点和显示点同步。
        this.cardinalControlPoints.forEach((point) => {
            if (point === this.ellipseCenterPoint || point === this.ellipsePointA || point === this.ellipsePointB) {
                return;
            }
            point.syncPositionSilently(point.getPosition().add(delta));
        });

        // 最后只刷新曲线几何，不重新根据单个拖拽点反推椭圆定义。
        this.updateGeometry();
    }


    /**
     * 获取当前椭圆定义快照
     * @returns 椭圆定义副本
     */
    public getEllipseDefinition(): Required<SketchEllipseDefinition> {
        return {
            ...this.ellipseDefinition,
            centerWorld: this.ellipseDefinition.centerWorld.clone(),
        };
    }


    /**
     * 判断当前椭圆对象是否允许拖动。
     * 说明：
     * 1. 先检查椭圆对象自身固定约束；
     * 2. 再检查端点、中心点和四向基准轴控制点是否固定；
     * 3. 只要任一关键控制点固定，就禁止整体拖动。
     * @returns 允许拖动返回 true，否则返回 false。
     */
    public override canDrag(): boolean {
        if (!super.canDrag()) {
            return false;
        }

        const checkedPointIds = new Set<string>();
        const pointsToCheck: SketchPointObject[] = [
            this.ellipsePointA,
            this.ellipsePointB,
            this.ellipseCenterPoint,
            ...this.cardinalControlPoints.values(),
        ];

        return !pointsToCheck.some((point) => {
            if (checkedPointIds.has(point.uuid)) {
                return false;
            }
            checkedPointIds.add(point.uuid);
            return point.isFixed();
        });
    }


    /**
     * 复制当前椭圆对象，返回与源对象完全解耦的新实例。
     * @param context 复制上下文。
     * @returns 当前椭圆对象的独立副本。
     */
    public cloneSelf(context?: SketchCloneContext): SketchEllipseObject {
        const clonedEllipse = new SketchEllipseObject({
            ...this.createCloneBaseOptions(),
            referencePlane: this.referencePlane,
            definition: this.getEllipseDefinition(),
            startPoint: this.ellipsePointA.cloneSelf(context),
            endPoint: this.ellipsePointB.cloneSelf(context),
            centerPoint: this.ellipseCenterPoint.cloneSelf(context),
        });
        this.registerObjectClone(context, clonedEllipse);

        // 复制已注册的基准轴控制点，保持椭圆控制行为一致。
        this.cardinalControlPoints.forEach((point, role) => {
            clonedEllipse.registerCardinalControlPoint(point.cloneSelf(context), role);
        });

        if (this.contour) {
            clonedEllipse.syncContour();
        }

        // 复制椭圆对象的单目标几何关系。
        this.cloneSingleTargetConstraintsTo(clonedEllipse, context);
        this.syncCommonDisplayStateToClone(clonedEllipse);
        return clonedEllipse;
    }


    protected getEdgeStartPoint(): SketchPointObject {
        return this.ellipsePointA;
    }

    protected getEdgeEndPoint(): SketchPointObject {
        return this.ellipsePointB;
    }

    protected setEdgeStartPoint(point: SketchPointObject): void {
        this.unregisterRelatedSketchPoint(this.ellipsePointA);
        this.ellipsePointA.detachEndpointRole();
        this.ellipsePointA = point;
        this.ellipsePointA.attachEndpointRole();
        this.registerRelatedSketchPoint(this.ellipsePointA);
    }

    protected setEdgeEndPoint(point: SketchPointObject): void {
        this.unregisterRelatedSketchPoint(this.ellipsePointB);
        this.ellipsePointB.detachEndpointRole();
        this.ellipsePointB = point;
        this.ellipsePointB.attachEndpointRole();
        this.registerRelatedSketchPoint(this.ellipsePointB);
    }

    /**
     * 获取椭圆中心点对象。
     * @returns 椭圆中心点对象。
     */
    public getCenterPoint(): SketchPointObject {
        return this.ellipseCenterPoint;
    }

    /**
     * 获取椭圆起点控制点。
     * @returns 椭圆起点控制点。
     */
    public getStartPoint(): SketchPointObject {
        return this.ellipsePointA;
    }

    /**
     * 获取椭圆终点控制点。
     * @returns 椭圆终点控制点。
     */
    public getEndPoint(): SketchPointObject {
        return this.ellipsePointB;
    }

    /**
     * 替换部分椭圆起点的拓扑点对象。
     *
     * 该方法只迁移端点身份，不改写椭圆参数定义；调用方应在完成拓扑保护后，
     * 再通过 `updateEllipseDefinition()` 统一刷新端点位置和显示几何。
     *
     * @param point 新起点对象。
     */
    public setStartPoint(point: SketchPointObject): void {
        if (point === this.ellipsePointA) {
            return;
        }

        this.ellipsePointA.unregisterConnectedEdge(this);
        this.setEdgeStartPoint(point);
        this.ellipsePointA.registerConnectedEdge(this);
    }

    /**
     * 替换部分椭圆终点的拓扑点对象。
     *
     * @param point 新终点对象。
     */
    public setEndPoint(point: SketchPointObject): void {
        if (point === this.ellipsePointB) {
            return;
        }

        this.ellipsePointB.unregisterConnectedEdge(this);
        this.setEdgeEndPoint(point);
        this.ellipsePointB.registerConnectedEdge(this);
    }

    /**
     * 按当前椭圆定义同步控制点位置。
     * 说明：
     * 1. 裁剪与撤销恢复链路会先更新椭圆定义，再要求对象把控制点回写到定义对应位置；
     * 2. 这里使用静默同步，避免快照恢复过程中重复触发约束求解；
     * 3. 若已经注册了四向基准轴控制点，也要一并同步，保证捕捉与显示一致。
     */
    public syncControlPointsFromDefinition(): void {
        this.syncEndpointPositionsFromDefinition();
        this.ellipseCenterPoint.syncPositionSilently(this.ellipseDefinition.centerWorld.clone());
        this.repositionAllCardinalControlPoints();
    }


    /**
     * 注册一个基准轴控制点。
     * 说明：
     * 1. 将控制点的角色写入点对象自身属性；
     * 2. 将控制点注册为本椭圆的关联边，使其拖拽时能触发 syncEndpointsToGeometry。
     * @param point 控制点对象。
     * @param role 控制点角色。
     */
    public registerCardinalControlPoint(point: SketchPointObject, role: EllipseControlPointRole): void {
        this.cardinalControlPoints.set(role, point);
        point.ellipseControlPointRole = role;
        if (role === EllipseControlPointRole.Center) {
            this.unregisterRelatedSketchPoint(this.ellipseCenterPoint);
            this.ellipseCenterPoint.detachCurveCenterControlPointRole();
            this.ellipseCenterPoint = point;
            this.ellipseCenterPoint.attachCurveCenterControlPointRole();
        } else if (
            role === EllipseControlPointRole.MajorPositive
            || role === EllipseControlPointRole.MajorNegative
            || role === EllipseControlPointRole.MinorPositive
            || role === EllipseControlPointRole.MinorNegative
        ) {
            point.isQuadrantPoint = true;
            // 轴端角色在点构造后才建立，注册完成后必须重新按角色解析尺寸，避免保留控制点初始缩放。
            point.restoreDefaultScale();
        }
        // 注册关联边，确保拖拽时 notifyConnectedLines 能回调到本椭圆。
        point.registerConnectedEdge(this);
        this.registerRelatedSketchPoint(point);
    }


    /**
     * 获取基准轴控制点只读快照。
     * 说明：
     * 1. 供持久化序列化按角色枚举基准轴控制点；
     * 2. 返回副本映射，调用方不得据此修改椭圆内部注册状态。
     * @returns 角色到控制点对象的只读映射。
     */
    public getCardinalControlPointsSnapshot(): ReadonlyMap<EllipseControlPointRole, SketchPointObject> {
        return new Map(this.cardinalControlPoints);
    }


    /**
     * 重写端点同步几何方法。
     * 说明：
     * 1. 当触发点为已注册的基准轴控制点时，执行椭圆定义重算和全部控制点重定位；
     * 2. 其他情况蒞给父类默认行为。
     * @param movedPoint 触发刷新的控制点。
     */
    public override syncEndpointsToGeometry(movedPoint?: SketchPointObject): void {
        // 拖拽重建过程中的递归回调，直接跳过。
        if (this.isRebuildingFromDrag) {
            return;
        }

        // 判断触发点是否为已注册的基准轴控制点。
        const draggedPoint = movedPoint;
        if (draggedPoint && isSketchPointObject(draggedPoint)) {
            const role = draggedPoint.ellipseControlPointRole;
            if (role && this.cardinalControlPoints.get(role) === draggedPoint) {
                // 先重建几何（更新椭圆定义及所有控制点位置）。
                this.rebuildFromDraggedControlPoint(draggedPoint, role);
                // 再刷新公共派生几何，关系和尺寸已由外层参数求解完成。
                super.syncEndpointsToGeometry(movedPoint);
                return;
            }
        }

        if (this.isPartialEllipse()) {
            if (draggedPoint === this.ellipsePointA) {
                this.rebuildPartialEllipseEndpointFromDraggedPoint('start');
                return;
            }
            if (draggedPoint === this.ellipsePointB) {
                this.rebuildPartialEllipseEndpointFromDraggedPoint('end');
                return;
            }
        }

        // 非基准轴控制点变更，蒞给父类默认行为。
        super.syncEndpointsToGeometry(movedPoint);
    }

    //------ 椭圆静默刷新按控制点重建定义 开始------
    /**
     * 仅刷新几何：先按基准轴控制点重建椭圆定义，再重画曲线并复位控制点。
     *
     * 椭圆的形状有两处表示——存储的 `ellipseDefinition` 与四个基准轴控制点。
     * 反推此前只挂在**拖拽**路径上，基类的静默刷新只重画曲线，于是外部写者
     * （参数求解写回、事务回滚）改完点位后椭圆纹丝不动，控制点与曲线脱节。
     * @param movedPoint 本轮外部写入的权威控制点；未指定时按正长短轴角色反解。
     * @param options 派生几何刷新选项。
     */
    public override refreshGeometryOnly(
        movedPoint?: SketchPointObject,
        options?: SketchEdgeGeometryRefreshOptions,
    ): void {
        const wasPartial = this.isPartialEllipse();

        this.rebuildDefinitionFromCardinalPoints(movedPoint);
        /**
         * 部分椭圆的弧段角度同样按端点反解。
         *
         * 少了这一步，下面的端点回贴会把刚被写到新位置的端点按**旧角度**拉回去，
         * 等于把外部写者的解直接吃掉（引用投影联合求解会解到椭圆另一侧）。
         */
        if (wasPartial) {
            this.rebuildAngleRangeFromEndpoints();
        }
        this.updateGeometry(options);
        if (wasPartial) {
            this.syncEndpointPositionsFromDefinition();
        }
        this.repositionAllCardinalControlPoints();
    }

    /**
     * 按当前起止端点反解弧段参数角（保持原走向）。
     */
    private rebuildAngleRangeFromEndpoints(): void {
        const startAngle = this.resolveParameterAngleFromWorldPoint(this.ellipsePointA.getPosition());
        const endAngle = this.resolveParameterAngleFromWorldPoint(this.ellipsePointB.getPosition());

        if (!Number.isFinite(startAngle) || !Number.isFinite(endAngle)) {
            return;
        }
        // 与端点拖拽同一道退化闸门：扫角塌成一点时保留原角度，不写脏定义。
        if (this.computeEllipseSweep(startAngle, endAngle, this.ellipseDefinition.clockwise)
            <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.partialSweepAngle) {
            return;
        }

        this.ellipseDefinition = {
            ...this.ellipseDefinition,
            startAngle,
            endAngle,
        };
    }

    /**
     * 按基准轴控制点重建椭圆定义（中心 + 长轴方向与半长轴 + 半短轴）。
     *
     * 与拖拽路径同一套投影口径：长轴控制点给出方向与 radiusX，
     * 短轴控制点在该方向的法向投影给出 radiusY。
     * @param authoritativePoint 本轮被直接改写的长轴或短轴控制点。
     */
    private rebuildDefinitionFromCardinalPoints(authoritativePoint?: SketchPointObject): void {
        const centerPoint = this.cardinalControlPoints.get(EllipseControlPointRole.Center);
        const authoritativeRole = authoritativePoint?.ellipseControlPointRole;
        const majorRole = (
            authoritativeRole === EllipseControlPointRole.MajorPositive
            || authoritativeRole === EllipseControlPointRole.MajorNegative
        ) && this.cardinalControlPoints.get(authoritativeRole) === authoritativePoint
            ? authoritativeRole
            : EllipseControlPointRole.MajorPositive;
        const minorRole = (
            authoritativeRole === EllipseControlPointRole.MinorPositive
            || authoritativeRole === EllipseControlPointRole.MinorNegative
        ) && this.cardinalControlPoints.get(authoritativeRole) === authoritativePoint
            ? authoritativeRole
            : EllipseControlPointRole.MinorPositive;
        const majorPoint = this.cardinalControlPoints.get(majorRole);
        const minorPoint = this.cardinalControlPoints.get(minorRole);

        if (!centerPoint || !majorPoint || !minorPoint) {
            return;
        }

        const centerWorld = centerPoint.getPosition();
        const localCenter = this.referencePlane.worldToLocal(centerWorld);
        const localMajor = this.referencePlane.worldToLocal(majorPoint.getPosition());
        const localMinor = this.referencePlane.worldToLocal(minorPoint.getPosition());
        // 负轴角色的世界向量指向长轴反方向，反解定义时必须翻回正长轴方向。
        const majorDirectionSign = majorRole === EllipseControlPointRole.MajorNegative ? -1 : 1;
        const majorDx = majorDirectionSign * (localMajor.x - localCenter.x);
        const majorDy = majorDirectionSign * (localMajor.y - localCenter.y);
        const radiusX = Math.hypot(majorDx, majorDy);

        if (radiusX < SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            return;
        }

        const rotation = Math.atan2(majorDy, majorDx);
        const minorDirectionSign = minorRole === EllipseControlPointRole.MinorNegative ? -1 : 1;
        const radiusY = Math.abs(minorDirectionSign * (
            (localMinor.x - localCenter.x) * -Math.sin(rotation)
            + (localMinor.y - localCenter.y) * Math.cos(rotation)
        ));

        if (radiusY < SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            return;
        }

        this.ellipseDefinition = {
            ...this.ellipseDefinition,
            centerWorld: centerWorld.clone(),
            radiusX,
            radiusY,
            rotation,
        };
    }
    //------ 椭圆静默刷新按控制点重建定义 结束------

    /**
     * 根据被拖拽的控制点重算椭圆定义并重定位全部控制点。
     * 说明：
     * 1. 拖拽中心控制点时，整体平移椭圆；
     * 2. 拖拽长轴控制点时，重算 radiusX 和 rotation；
     * 3. 拖拽短轴控制点时，重算 radiusY；
     * 4. 重算后重建几何并将全部控制点复位到椭圆边缘。
     * @param draggedPoint 被拖拽的控制点。
     * @param role 控制点角色。
     */
    private rebuildFromDraggedControlPoint(
        draggedPoint: SketchPointObject,
        role: EllipseControlPointRole,
    ): void {
        this.isRebuildingFromDrag = true;
        try {
            if (role === EllipseControlPointRole.Center) {
                // 拖拽中心控制点：整体平移椭圆。
                const newCenter = draggedPoint.getPosition();
                const delta = newCenter.clone().sub(this.ellipseDefinition.centerWorld);
                this.ellipseDefinition.centerWorld = newCenter.clone();

                // 同步内置端点位置。
                const nextPointA = this.ellipsePointA.getPosition().add(delta);
                const nextPointB = this.ellipsePointB.getPosition().add(delta);
                this.ellipsePointA.setPosition(nextPointA);
                this.ellipsePointB.setPosition(nextPointB);

                // 重建椭圆几何。
                this.updateGeometry();

                if (this.isPartialEllipse()) {
                    this.syncEndpointPositionsFromDefinition();
                }

                // 重定位所有基准轴控制点（不含中心点自身）。
                this.repositionAllCardinalControlPoints();

                // 同步轮廓。
                if (this.contour) {
                    this.syncContour();
                }
                return;
            }

            const center = this.ellipseDefinition.centerWorld;
            const localCenter = this.referencePlane.worldToLocal(center);
            const localNewPos = this.referencePlane.worldToLocal(draggedPoint.getPosition());

            if (role === EllipseControlPointRole.MajorPositive || role === EllipseControlPointRole.MajorNegative) {
                // 拖拽长轴控制点：重算 radiusX 和 rotation。
                // MajorPositive: 从中心指向点为长轴方向。
                // MajorNegative: 从点指向中心为长轴方向。
                const sign = role === EllipseControlPointRole.MajorPositive ? 1 : -1;
                const majorDx = sign * (localNewPos.x - localCenter.x);
                const majorDy = sign * (localNewPos.y - localCenter.y);
                const newRadiusX = Math.hypot(majorDx, majorDy);
                if (newRadiusX < SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
                    return;
                }
                this.ellipseDefinition.radiusX = newRadiusX;
                this.ellipseDefinition.rotation = Math.atan2(majorDy, majorDx);
            } else {
                // 拖拽短轴控制点：仅重算 radiusY，保持 rotation 不变。
                // 将拖拽位置投影到当前短轴方向上计算距离。
                const rotation = this.ellipseDefinition.rotation;
                const minorDirX = -Math.sin(rotation);
                const minorDirY = Math.cos(rotation);
                const dx = localNewPos.x - localCenter.x;
                const dy = localNewPos.y - localCenter.y;
                const newRadiusY = Math.abs(dx * minorDirX + dy * minorDirY);
                this.ellipseDefinition.radiusY = newRadiusY;
            }

            // 重建椭圆几何。
            this.updateGeometry();

            if (this.isPartialEllipse()) {
                this.syncEndpointPositionsFromDefinition();
            }

            // 将全部控制点复位到椭圆边缘的正确位置。
            this.repositionAllCardinalControlPoints();

            // 同步轮廓。
            if (this.contour) {
                this.syncContour();
            }
        } finally {
            this.isRebuildingFromDrag = false;
        }
    }

    /**
     * 将全部基准轴控制点复位到椭圆定义对应的位置。
     * 说明：
     * 1. 根据当前椭圆定义计算四向轴端点世界坐标；
     * 2. 通过点对象公开落位 API 写入并刷新共享端点的派生几何。
     */
    private repositionAllCardinalControlPoints(): void {
        const center = this.ellipseDefinition.centerWorld;
        const {radiusX, radiusY, rotation} = this.ellipseDefinition;

        // 计算参考平面上的长/短轴方向向量。
        const xAxis = this.referencePlane.xAxis.clone().normalize();
        const yAxis = this.referencePlane.yAxis.clone().normalize();
        const cosRot = Math.cos(rotation);
        const sinRot = Math.sin(rotation);
        const majorDir = xAxis.clone().multiplyScalar(cosRot).add(yAxis.clone().multiplyScalar(sinRot)).normalize();
        const minorDir = xAxis.clone().multiplyScalar(-sinRot).add(yAxis.clone().multiplyScalar(cosRot)).normalize();

        // 各角色对应的目标位置。
        const targetPositions = new Map<EllipseControlPointRole, THREE.Vector3>([
            [EllipseControlPointRole.Center, center.clone()],
            [EllipseControlPointRole.MajorPositive, center.clone().add(majorDir.clone().multiplyScalar(radiusX))],
            [EllipseControlPointRole.MajorNegative, center.clone().sub(majorDir.clone().multiplyScalar(radiusX))],
            [EllipseControlPointRole.MinorPositive, center.clone().add(minorDir.clone().multiplyScalar(radiusY))],
            [EllipseControlPointRole.MinorNegative, center.clone().sub(minorDir.clone().multiplyScalar(radiusY))],
        ]);

        // 逐个复位控制点。
        for (const [role, point] of this.cardinalControlPoints) {
            const targetPos = targetPositions.get(role);
            if (targetPos && !point.getPosition().equals(targetPos)) {
                // 这些点由当前椭圆定义派生；回贴时不得反向触发“控制点驱动定义”链路。
                point.syncPositionSilently(targetPos);
            }
        }
    }

    /**
     * 按椭圆参数角解析世界坐标。
     * 说明：
     * 1. 先在参考面局部坐标系计算未旋转椭圆上的点；
     * 2. 再围绕局部圆心应用椭圆整体旋转；
     * 3. 最后转换回世界坐标，供端点同步与裁剪恢复链路复用。
     * @param angle 椭圆参数角。
     * @returns 对应世界坐标。
     */
    private resolveWorldPointByAngle(angle: number): THREE.Vector3 {
        const localCenter = this.referencePlane.worldToLocal(this.ellipseDefinition.centerWorld);
        const localPoint = new THREE.Vector2(
            localCenter.x + this.ellipseDefinition.radiusX * Math.cos(angle),
            localCenter.y + this.ellipseDefinition.radiusY * Math.sin(angle),
        ).rotateAround(localCenter, this.ellipseDefinition.rotation);

        return this.referencePlane.localToWorld(localPoint);
    }

    /**
     * 将弧起点与弧终点同步到当前椭圆定义对应的位置。
     * 说明：
     * 1. 部分椭圆的端点表达弧段范围，必须跟随 `startAngle/endAngle`；
     * 2. 完整椭圆的 start/end 在历史链路中承担轴标记点职责，因此调用方需先确认语义；
     * 3. 静默写入避免端点回贴时再次触发约束求解。
     */
    private syncEndpointPositionsFromDefinition(): void {
        this.ellipsePointA.syncPositionSilently(this.resolveWorldPointByAngle(this.ellipseDefinition.startAngle));
        this.ellipsePointB.syncPositionSilently(this.resolveWorldPointByAngle(this.ellipseDefinition.endAngle));
    }

    /**
     * 判断当前定义是否为部分椭圆。
     * @returns 非完整扫掠时返回 true。
     */
    public isPartialEllipse(): boolean {
        const sweep = this.computeEllipseSweep(
            this.ellipseDefinition.startAngle,
            this.ellipseDefinition.endAngle,
            this.ellipseDefinition.clockwise,
        );
        return Math.abs(sweep - Math.PI * 2) > SKETCH_GEOMETRY_TOLERANCE_CONFIG.fullSweepAngle;
    }

    /**
     * 根据世界坐标反解椭圆参数角。
     * 说明：
     * 1. 先把世界点投影到草图参考面的局部二维坐标；
     * 2. 再消除椭圆整体旋转；
     * 3. 最后按长短半轴归一化并求 `atan2`，得到 THREE.EllipseCurve 使用的参数角。
     * @param pointWorld 世界坐标点。
     * @returns 椭圆参数角。
     */
    private resolveParameterAngleFromWorldPoint(pointWorld: THREE.Vector3): number {
        const localCenter = this.referencePlane.worldToLocal(this.ellipseDefinition.centerWorld);
        const localPoint = this.referencePlane.worldToLocal(pointWorld);
        const dx = localPoint.x - localCenter.x;
        const dy = localPoint.y - localCenter.y;
        const cosRotation = Math.cos(this.ellipseDefinition.rotation);
        const sinRotation = Math.sin(this.ellipseDefinition.rotation);
        const alignedX = dx * cosRotation + dy * sinRotation;
        const alignedY = -dx * sinRotation + dy * cosRotation;
        const normalizedX = this.ellipseDefinition.radiusX <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength
            ? 0
            : alignedX / this.ellipseDefinition.radiusX;
        const normalizedY = this.ellipseDefinition.radiusY <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength
            ? 0
            : alignedY / this.ellipseDefinition.radiusY;

        return Math.atan2(normalizedY, normalizedX);
    }

    /**
     * 根据被拖拽的部分椭圆端点重建弧段角度。
     * @param role 被拖拽的端点角色。
     */
    private rebuildPartialEllipseEndpointFromDraggedPoint(role: 'start' | 'end'): void {
        const draggedEndpoint = role === 'start' ? this.ellipsePointA : this.ellipsePointB;
        const nextAngle = this.resolveParameterAngleFromWorldPoint(draggedEndpoint.getPosition());
        const nextStartAngle = role === 'start' ? nextAngle : this.ellipseDefinition.startAngle;
        const nextEndAngle = role === 'end' ? nextAngle : this.ellipseDefinition.endAngle;
        const nextSweep = this.computeEllipseSweep(nextStartAngle, nextEndAngle, this.ellipseDefinition.clockwise);

        if (nextSweep <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.partialSweepAngle) {
            return;
        }

        this.ellipseDefinition = {
            ...this.ellipseDefinition,
            startAngle: nextStartAngle,
            endAngle: nextEndAngle,
        };
        this.updateGeometry();
        this.syncEndpointPositionsFromDefinition();
        this.repositionAllCardinalControlPoints();
        if (this.contour) {
            this.syncContour();
        }
        this.markOwnerSketchGroupDirty('partial-ellipse-endpoint-dragged');
    }

    /**
     * 计算椭圆弧按当前方向扫过的角度。
     * @param startAngle 起始参数角。
     * @param endAngle 终止参数角。
     * @param clockwise 是否顺时针。
     * @returns 正向扫掠角。
     */
    private computeEllipseSweep(startAngle: number, endAngle: number, clockwise: boolean): number {
        if (clockwise) {
            let sweep = startAngle - endAngle;
            if (sweep <= 0) {
                sweep += Math.PI * 2;
            }
            return sweep;
        }

        let sweep = endAngle - startAngle;
        if (sweep <= 0) {
            sweep += Math.PI * 2;
        }
        return sweep;
    }


    /**
     * 基于当前椭圆定义重建几何
     * @param options 派生几何刷新选项。
     */
    protected updateGeometry(options?: SketchEdgeGeometryRefreshOptions): void {
        const localCenter = this.referencePlane.worldToLocal(this.ellipseDefinition.centerWorld);
        const curve = new THREE.EllipseCurve(
            localCenter.x,
            localCenter.y,
            this.ellipseDefinition.radiusX,
            this.ellipseDefinition.radiusY,
            this.ellipseDefinition.startAngle,
            this.ellipseDefinition.endAngle,
            this.ellipseDefinition.clockwise,
            this.ellipseDefinition.rotation,
        );

        const localPoints = curve.getPoints(this.ellipseDefinition.segments);
        const worldPoints = localPoints.map((point) =>
            this.referencePlane.localToWorld(new THREE.Vector2(point.x, point.y)),
        );

        const shouldRebuildRaycastAcceleration = this.syncSampledLineGeometry(worldPoints);
        this.geometry.computeBoundingBox();
        this.geometry.computeBoundingSphere();
        if (shouldRebuildRaycastAcceleration) {
            // 采样点数量变化后，新几何需要重新建立与新顶点缓冲匹配的射线加速结构。
            ensureRaycastAccelerationForObjectTree(this);
        } else {
            // 同拓扑原位更新后仅校正既有 BVH，保持拾取结构与最新顶点一致。
            refitRaycastAccelerationForGeometry(this.geometry);
        }

        if (this.shouldSyncLineDistancesForMaterial(this.material)) {
            this.computeLineDistances();
        }


        // 椭圆几何移动后必须同步已有轮廓实例，否则上色轮廓会停留在旧位置。
        this.refreshContourGeometry(options);

    }


    /**
     * 收集当前椭圆线几何的世界坐标点序列。
     *
     * 设计说明：
     * 1. 椭圆轮廓与圆轮廓一样直接来源于当前线几何采样点；
     * 2. 轮廓刷新时反读最新 BufferGeometry，避免重复维护另一套椭圆采样逻辑；
     * 3. 返回独立坐标对象，避免轮廓构建过程修改源几何数据。
     *
     * @returns 椭圆几何顶点列表。
     */
    private collectGeometryPoints(): THREE.Vector3[] {
        const position = this.geometry.getAttribute('position');
        if (!position || position.count <= 0) {
            return [];
        }
        const points: THREE.Vector3[] = [];
        for (let index = 0; index < position.count; index += 1) {
            points.push(new THREE.Vector3(
                position.getX(index),
                position.getY(index),
                position.getZ(index),
            ));
        }
        return points;
    }

    /**
     * 刷新已创建椭圆轮廓的几何点集。
     *
     * 设计说明：
     * 1. 移动实体、属性编辑和控制点拖拽都会最终进入椭圆几何刷新；
     * 2. 这里复用同一出口同步轮廓，保证已有轮廓 Mesh 不会与源椭圆脱节；
     * 3. 若当前椭圆已不再满足闭合轮廓条件，则清理显示轮廓。
     * @param options 派生几何刷新选项。
     */
    private refreshContourGeometry(options?: SketchEdgeGeometryRefreshOptions): void {
        if (!this.contour) {
            return;
        }
        if (this.isPartialEllipse()) {
            if (this.closedCollection && !isSketchSlotObject(this.closedCollection)) {
                this.requestCompositeHostContourRefresh(this.closedCollection, options);
            }
            return;
        }
        if (!this.canCreateContour()) {
            this.clearContourDisplay();
            return;
        }
        const points = this.collectGeometryPoints();
        if (points.length < 3) {
            return;
        }
        this.contour.updatePoints(points);
    }


    /**
     * 绑定部分椭圆所属的闭合轮廓宿主。
     * 说明：
     * 1. 完整椭圆是自闭合实体，不经过该方法；
     * 2. 部分椭圆作为开放边进入闭合集合或槽口时，由该字段记录唯一宿主；
     * 3. 这里只保存拓扑宿主并复用宿主轮廓，避免重复持有和释放同一个轮廓资源。
     * @param geometry 目标闭合集合或槽口宿主。
     * @throws 当当前部分椭圆已绑定其它闭合宿主时抛出错误。
     */
    public attachClosedCollection(geometry: SketchCollectionObject | SketchSlotObject): void {
        if (this.closedCollection && this.closedCollection !== geometry) {
            throw new Error('当前部分椭圆已关联其他封闭线段几何对象。');
        }
        this.closedCollection = geometry;
        if (isSketchSlotObject(geometry)) {
            this.contour = geometry.contour;
            return;
        }
        this.contour = geometry.getContour();
    }

    /**
     * 解绑部分椭圆所属的闭合轮廓宿主。
     * @param geometry 若传入，仅在匹配当前宿主时解绑。
     */
    public detachClosedCollection(geometry?: SketchCollectionObject | SketchSlotObject): void {
        if (geometry && this.closedCollection !== geometry) {
            return;
        }
        this.closedCollection = null;
        this.clearContour();
    }

    /**
     * 判断当前椭圆是否可创建或恢复闭合轮廓。
     * @returns 完整椭圆自身闭合，或部分椭圆已绑定闭合宿主时返回 true。
     */
    public canCreateContour(): boolean {
        if (this.isConstruction) {
            return false;
        }
        return !this.isPartialEllipse() || this.closedCollection !== null;
    }

    /**
     * 基于当前椭圆几何创建轮廓对象。

     * @returns 创建后的轮廓对象。
     * @throws 当椭圆不是闭合曲线时抛出错误。
     * @throws 当更新后的椭圆几何缺少至少三个顶点时抛出错误。
     */
    public createContour(): SketchContour {
        if (!this.canCreateContour()) {
            throw new Error('当前椭圆未形成有效闭合曲线，无法创建轮廓。');
        }
        if (this.isPartialEllipse()) {
            return this.createContourFromHost(this.closedCollection, '部分椭圆');
        }
        this.updateGeometry();
        const position = this.geometry.getAttribute('position');
        if (!position || position.count < 3) {
            throw new Error('椭圆几何顶点数量不足，无法创建轮廓。');
        }
        const contour = new SketchContour({
            source:this
        });
        this.contour = contour;
        return contour;
    }

    public syncContour(): void {
        if (this.isPartialEllipse()) {
            if (!this.closedCollection) {
                this.clearContourDisplay();
                return;
            }
            this.closedCollection.syncContour();
            this.contour = isSketchSlotObject(this.closedCollection)
                ? this.closedCollection.contour
                : this.closedCollection.getContour();
            return;
        }
        if (!this.canCreateContour()) {
            this.clearContourDisplay();
            return;
        }
        this.clearContourDisplay();
        const contour = this.createContour();
        this.contour = contour;
        this.add(contour);
    }

    /**
     * 清理当前轮廓显示。
     * 说明：
     * 1. 仅移除当前椭圆对象上挂载的轮廓实例；
     * 2. 同步清空轮廓引用；
     * 3. 不修改椭圆本体几何。
     */
    public clearContourDisplay(): void {
        const contour = this.contour;
        if (contour && contour.parent === this) {
            this.remove(contour);
        }
        if (contour?.source === this) {
            disposeSketchContour(contour);
        }
        this.clearContour();
    }

    /**
     * 获取椭圆的象限点（四个基准轴控制点：长轴正负端和短轴正负端）。
     * @returns 象限点数组。
     */
    public getQuadrantPoints(): SketchPointObject[] {
        const result: SketchPointObject[] = [];
        for (const [, point] of this.cardinalControlPoints) {
            if (point.isQuadrantPoint) {
                result.push(point);
            }
        }
        return result;
    }
}
